import React, { useEffect, useMemo, useState } from 'react';
import { Select, Space, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
const { Option } = Select;
import OrganizationFilter from '../../OrganizationFilter';
import organizationEventEmitter from '../../../../../../../utilities/organizationEmmiter';
import { getFromStorage } from '../../../../../../../lib/storage';
import { getSRTypeList } from 'src/modules/SuperAdmin/store/actions/service_request';
import { listAgentsAction } from 'src/modules/SuperAdmin/store/actions/agents.action';

const ListPromptsFilters = ({ filters, setFilters }) => {
  const {
    conditions,
    conditionsLoading,
    specialties,
    currentPractitioner,
    serviceRequestTypes,
    srTypeListLoading,
    agentsList,
    agentsLoading,
  } = useSelector((state) => ({
    conditions: state?.conditionState?.conditionsDropdown,
    conditionsLoading: state?.conditionState?.conditionsLoading,
    specialties: state?.specialtyState?.getAllSpecialtiesList,
    currentPractitioner: state?.practitionerState?.currentPractitioner,
    serviceRequestTypes: state?.serviceRequestState?.srTypeList,
    srTypeListLoading: state?.serviceRequestState?.srTypeListLoading,
    agentsList: state?.agentsState?.agentsList,
    agentsLoading: state?.agentsState?.agentsLoading,
  }));
  const {
    prompts,
    promptsLoading,
    llms,
    llmsLoading,
    promptScreens,
    promptScreensLoading,
  } = useSelector((state) => state?.promptsState);
  const dispatch = useDispatch();
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  // Filter conditions based on selected specialty
  const filteredConditions = useMemo(() => {
    if (!conditions || !Array.isArray(conditions)) return [];

    // If no specialty is selected, return all conditions
    if (!filters.specialty_id) {
      return conditions;
    }

    // Filter conditions by specialty_id
    return conditions.filter(
      (condition) => condition.specialty_id === filters.specialty_id
    );
  }, [conditions, filters.specialty_id]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedOrganizationUUID) {
      setFilters({
        ...filters,
        service_request_type_id: undefined,
        agent_id: undefined,
      });
      dispatch(getSRTypeList(selectedOrganizationUUID));
      dispatch(listAgentsAction(selectedOrganizationUUID));
    }
  }, [selectedOrganizationUUID, dispatch]);

  const filteredSRTypeList = useMemo(() => {
    if (!serviceRequestTypes || !Array.isArray(serviceRequestTypes)) return [];
    return serviceRequestTypes;
  }, [serviceRequestTypes]);

  const filteredAgentsList = useMemo(() => {
    if (!agentsList?.agents || !Array.isArray(agentsList?.agents)) return [];
    return agentsList.agents;
  }, [agentsList]);

  return (
    <Space style={{ marginBottom: '16px' }} wrap>
      <div className="flex flex-row gap-2">
        <div className="bg-white shadow-sm rounded-xl w-60 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Specialty"
            onChange={(value) => {
              // Clear condition filter when specialty changes
              setFilters({
                ...filters,
                specialty_id: value,
                condition_id: undefined,
              });
            }}
            value={filters.specialty_id}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={specialties?.map((spec) => ({
              label: spec.specialty_name,
              value: spec.specialty_id,
            }))}
          />
        </div>
        <div className="bg-white shadow-sm rounded-xl w-60 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Condition"
            onChange={(value) =>
              setFilters({ ...filters, condition_id: value })
            }
            value={filters.condition_id}
            loading={conditionsLoading}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredConditions?.map((cond) => ({
              label: cond.condition_name,
              value: cond.condition_id,
            }))}
          />
        </div>
        <div className="bg-white shadow-sm rounded-xl w-[28rem] flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Prompt Screen"
            onChange={(value) =>
              setFilters({ ...filters, prompt_screens_id: value })
            }
            value={filters.prompt_screens_id}
            loading={promptScreensLoading}
            variant="borderless"
            allowClear
            showSearch
            optionLabelProp="label"
            filterOption={(input, option) => {
              const children = option.children;
              const textContent =
                children?.props?.children?.[0]?.props?.children || '';
              const descContent =
                children?.props?.children?.[1]?.props?.children || '';
              return (
                textContent.toLowerCase().includes(input.toLowerCase()) ||
                descContent.toLowerCase().includes(input.toLowerCase())
              );
            }}
          >
            {promptScreens?.prompt_screens?.map((screen) => (
              <Option
                key={screen.prompt_screens_id}
                value={screen.prompt_screens_id}
                label={screen.prompt_screen}
              >
                <div className="flex flex-col">
                  <div className="font-medium">{screen.prompt_screen}</div>
                  {screen.description && (
                    <div className="text-xs text-gray-500">
                      {screen.description}
                    </div>
                  )}
                </div>
              </Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center">
          <span
            onClick={() => {
              setFilters({ page: 1, per_page: 20 });
            }}
            className="w-[150px] text-sm cursor-pointer hover:underline"
          >
            Clear filters
          </span>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <OrganizationFilter />
        <div className="bg-white shadow-sm rounded-xl w-80 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Service Request Type"
            onChange={(value) =>
              setFilters({ ...filters, service_request_type_id: value })
            }
            value={filters.service_request_type_id}
            loading={srTypeListLoading}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredSRTypeList?.map((srType) => ({
              label: srType.service_request_type,
              value: srType.service_request_type_id,
            }))}
          />
        </div>
        <div className="bg-white shadow-sm rounded-xl w-60 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Agent"
            onChange={(value) => setFilters({ ...filters, agent_id: value })}
            value={filters.agent_id}
            loading={agentsLoading}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredAgentsList?.map((agent) => ({
              label: agent.name,
              value: agent.agent_id,
            }))}
          />
        </div>
      </div>
    </Space>
  );
};

export default ListPromptsFilters;
